import { PropsWithChildren, ReactNode, useEffect, useMemo } from 'react';
import { AuthProvider as BaseAuthProvider, useAuth } from 'react-oidc-context';

import { MantineProvider, MantineTheme } from '@mantine/core';

import { AsyncStorage } from 'oidc-client-ts';

import { onSigninCallback } from './helpers';
import { useReloadOnAuthError } from './hooks';
import { initUserManager } from './user-manager';

export interface AuthProviderProps extends PropsWithChildren {
  clientId: string;
  theme?: MantineTheme;
  authority?: string;
  store?: AsyncStorage | Storage;
  loadingView?: () => ReactNode;
  onTokensRefresh?: () => void;
}

export function AuthProvider({ children, theme, authority, clientId, store, loadingView, onTokensRefresh }: AuthProviderProps) {
  const userManager = useMemo(() => initUserManager({
    authority: authority || process.env.PUBLIC_OIDC_AUTHORITY!,
    clientId,
    store,
  }), [authority, clientId, store]);

  useEffect(() => {
    const onTokensRefreshCallback = () => onTokensRefresh?.();
    const onSessionErrorCallback = () => userManager.signinRedirect();

    userManager.events.addUserLoaded(onTokensRefreshCallback);
    userManager.events.addUserSessionChanged(onTokensRefreshCallback);
    userManager.events.addSilentRenewError(onSessionErrorCallback);

    return () => {
      userManager.events.removeUserLoaded(onTokensRefreshCallback);
      userManager.events.removeUserSessionChanged(onTokensRefreshCallback);
      userManager.events.removeSilentRenewError(onSessionErrorCallback);
    };
  }, [userManager, onTokensRefresh]);

  return (
    <MantineProvider theme={theme}>
      <BaseAuthProvider
        userManager={userManager}
        onSigninCallback={onSigninCallback}
      >
        <AuthProviderInternals loadingView={loadingView}>
          {children}
        </AuthProviderInternals>
      </BaseAuthProvider>
    </MantineProvider>
  );
}

type AuthProviderInternalsProps = Pick<AuthProviderProps, 'loadingView' | 'children'>;

function AuthProviderInternals({ children, loadingView }: AuthProviderInternalsProps) {
  const { isLoading } = useAuth();

  useReloadOnAuthError();

  return isLoading
    ? <>{loadingView?.()}</>
    : <>{children}</>;
}
